import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          message: formData.message,
          lead_source: 'contact_page'
        })
      });

      let data;
      try {
        data = await response.json();
      } catch {
        data = { success: false, message: 'Server error. Please email us at americanprimeforyou@gmail.com' };
      }

      if (response.ok && data.success) {
        setSubmitStatus({ success: true, message: "Message sent! We'll get back to you within 24 hours." });
        setFormData({ name: '', email: '', company: '', message: '' });
        if (window.trackFormSubmission) window.trackFormSubmission('contact_form');
      } else {
        setSubmitStatus({ success: false, message: data.message || 'Something went wrong. Please email us at americanprimeforyou@gmail.com' });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus({ success: false, message: 'Unable to send. Please email us at americanprimeforyou@gmail.com' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactSchema = {
    "@type": "ContactPage",
    "@id": "https://americanprimellc.com/contact#contactpage",
    "url": "https://americanprimellc.com/contact",
    "name": "Contact American Prime LLC",
    "description": "Get in touch with American Prime LLC for AI automation services",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-203-589-9774",
      "contactType": "customer service",
      "email": "info@americanprimellc.com",
      "availableLanguage": "English"
    }
  };

  return (
    <>
      <SEOHead
        title="Contact Us | American Prime LLC - AI Automation Agency"
        description="Get in touch with American Prime LLC. Contact our team for AI automation services, pricing, and strategy calls."
        schema={contactSchema}
      />
      <div className="min-h-screen pt-20" style={{ background: 'var(--bg-primary)' }}>
        {/* Hero */}
        <section className="py-16 px-4" style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-subtle)' }}>
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
              style={{ background: 'rgba(212,175,55,0.1)', color: 'var(--brand-primary)', border: '1px solid rgba(212,175,55,0.2)' }}>
              <Phone size={14} />
              <a href="tel:+12035899774" style={{ color: 'var(--brand-primary)', textDecoration: 'none' }}>+1 (203) 589-9774</a>
            </div>
            <h1 className="heading-1 mb-6" style={{ color: 'var(--text-primary)' }}>
              Let's Transform<br />
              <span style={{ color: 'var(--brand-primary)' }}>Your Business</span>
            </h1>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Get in touch with our team. We typically respond within 24 hours.
            </p>
          </div>
        </section>

        {/* Contact Content */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

              {/* Left: Info */}
              <div className="space-y-8">
                <div>
                  <h2 className="heading-2 mb-6" style={{ color: 'var(--text-primary)' }}>Get in Touch</h2>
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-lg" style={{ background: 'rgba(212,175,55,0.1)' }}>
                        <Phone size={20} style={{ color: 'var(--brand-primary)' }} />
                      </div>
                      <div>
                        <div className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Phone</div>
                        <a href="tel:+12035899774" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>+1 (203) 589-9774</a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-lg" style={{ background: 'rgba(212,175,55,0.1)' }}>
                        <Mail size={20} style={{ color: 'var(--brand-primary)' }} />
                      </div>
                      <div>
                        <div className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Email</div>
                        <a href="mailto:info@americanprimellc.com" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>info@americanprimellc.com</a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-lg" style={{ background: 'rgba(212,175,55,0.1)' }}>
                        <MapPin size={20} style={{ color: 'var(--brand-primary)' }} />
                      </div>
                      <div>
                        <div className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Service Area</div>
                        <p style={{ color: 'var(--text-secondary)' }}>United States / North America</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Book Demo */}
                <div className="p-6 rounded-xl" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)' }}>
                  <h3 className="heading-3 mb-3" style={{ color: 'var(--text-primary)' }}>Schedule a Demo</h3>
                  <p className="body-medium mb-4" style={{ color: 'var(--text-secondary)' }}>
                    Book a time that works for you and see American Prime in action.
                  </p>
                  <a href="https://calendly.com/americanprimeforyou" target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold"
                    style={{ background: 'var(--brand-primary)', color: '#000000', textDecoration: 'none' }}>
                    <Send size={16} />
                    Book a Free Strategy Call
                  </a>
                </div>
              </div>

              {/* Right: Form */}
              <div className="p-8 rounded-xl" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-subtle)' }}>
                <h2 className="heading-2 mb-6" style={{ color: 'var(--text-primary)' }}>Send Us a Message</h2>

                {submitStatus && (
                  <div className="mb-6 p-4 rounded-lg flex items-start gap-3"
                    style={{
                      background: submitStatus.success ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                      border: `1px solid ${submitStatus.success ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`
                    }}>
                    {submitStatus.success && <CheckCircle size={20} style={{ color: '#22c55e', flexShrink: 0 }} />}
                    <p style={{ color: submitStatus.success ? '#22c55e' : '#ef4444', margin: 0 }}>
                      {submitStatus.message}
                    </p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Name *</label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required
                      placeholder="Your full name" className="w-full px-4 py-3 rounded-lg text-sm"
                      style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)', outline: 'none' }} />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Email *</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required
                      placeholder="your@email.com" className="w-full px-4 py-3 rounded-lg text-sm"
                      style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)', outline: 'none' }} />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Company</label>
                    <input type="text" id="company" name="company" value={formData.company} onChange={handleChange}
                      placeholder="Your company name" className="w-full px-4 py-3 rounded-lg text-sm"
                      style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)', outline: 'none' }} />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Message *</label>
                    <textarea id="message" name="message" value={formData.message} onChange={handleChange} required
                      rows={5} placeholder="Tell us about your project..." className="w-full px-4 py-3 rounded-lg text-sm resize-none"
                      style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)', outline: 'none' }} />
                  </div>
                  <button type="submit" disabled={isSubmitting} className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold"
                    style={{ background: isSubmitting ? 'var(--bg-primary)' : 'var(--brand-primary)', color: isSubmitting ? 'var(--text-muted)' : '#000000', border: '1px solid var(--border-subtle)', cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>
                    {isSubmitting ? <><Loader2 className="animate-spin" size={16} />Sending...</> : <><Send size={16} />Send Message</>}
                  </button>
                </form>
              </div>

            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Contact;
